import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import axios from 'axios'
import FinanceDetails from '../../shared/finance/FinanceDetails'
import FileList from '../../shared/file-upload/FileList'
import { buildFileDownloadUrl, getApiBaseUrl } from '../../utils/apiConfig'
import { getStorageValues } from '../../shared/CommonFunctions'
import { useMasterData } from '../../utils/useMasterData'
import { calculateReducingRateSummary, parseFinanceValues, validateFinanceValues } from '../../shared/finance/loanCalculator'

const INITIAL_FORM_STATE = {
  FinanceRegisterId: '',
  SalesId: '',
  SalesNumber: '',
  CustomerName: '',
  MobileNo: '',
  EmailId: '',
  CustomerAddress: '',
  VehicleId: '',
  VehicleLabel: '',
  VehicleCost: '',
  ChassisNo: '',
  EngineNo: '',
  FinancePartnerId: '',
  DownPayment: '',
  EMITenure: '',
  ProcessingFees: '',
  InterestRate: '',
  EMIAmount: '',
  LoanApplicationNo: '',
  StatusId: ''
}

function EditFinanceRegister({ FinanceRegisterData, sendDataToParent, onLoanSummary, onLoanSummaryApply }) {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE)
  const [financePartners, setFinancePartners] = useState([])
  const [financePartnersError, setFinancePartnersError] = useState('')
  const [selectedFinancePartnerId, setSelectedFinancePartnerId] = useState(null)
  const [loanSummaryError, setLoanSummaryError] = useState('')
  const [customerFiles, setCustomerFiles] = useState([])
  const { items: interestRateItems, loading: interestRateLoading } = useMasterData('interestRates')

  const interestRateOptions = useMemo(() => {
    return (interestRateItems || []).map((item) => ({
      value: item.ValueName ?? '',
      label: item.DisplayName || item.ValueName || '',
      id: item.MasterValueId
    }))
  }, [interestRateItems])

  const selectedInterestRate = useMemo(() => {
    if (!formData.InterestRate) return null
    const valueStr = formData.InterestRate.toString()
    return interestRateOptions.find((opt) => opt.value?.toString() === valueStr) || null
  }, [formData.InterestRate, interestRateOptions])

  const updateFieldValue = useCallback((name, value, options = {}) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value }
      if (!options.skipParent) {
        sendDataToParent((prevState) => ({ ...prevState, [name]: value }))
      }
      return next
    })
  }, [sendDataToParent])

  const fetchFinancePartners = useCallback(async () => {
    try {
      setFinancePartnersError('')
      const response = await axios.get(`${getApiBaseUrl()}/financepartner/getAll`, {
        params: { UserId: getStorageValues('userId') }
      })
      const payload = response?.data?.data
      const rows = Array.isArray(payload?.[0]) ? payload[0] : Array.isArray(payload) ? payload : []
      setFinancePartners(rows)
    } catch (error) {
      setFinancePartners([])
      setFinancePartnersError('Unable to load finance partners.')
    }
  }, [])

  const fetchCustomerDocuments = useCallback(async (customerId) => {
    if (!customerId) {
      setCustomerFiles([])
      return
    }
    try {
      const response = await axios.post(`${getApiBaseUrl()}/file/list`, {
        entityId: customerId,
        entityType: 'customer'
      })
      if (response.status === 200) {
        setCustomerFiles(response.data.data || [])
      }
    } catch (error) {
      console.log(error)
      setCustomerFiles([])
    }
  }, [])

  const handleFinancePartnerClick = (partner) => {
    if (!partner) return
    const rate = partner.InterestRate ?? ''
    const downPayment = partner.MinimumDownPayment ?? ''
    updateFieldValue('InterestRate', rate === null ? '' : rate.toString())
    updateFieldValue('DownPayment', downPayment === null ? '' : downPayment.toString())
    updateFieldValue('EMIAmount', '')
    updateFieldValue('FinancePartnerId', partner.FinancePartnerId ?? '')
    const key = partner.FinancePartnerId ?? partner.FinancePartnerName ?? null
    setSelectedFinancePartnerId(key)
  }

  const parsedFinanceValues = useMemo(() => parseFinanceValues(formData), [
    formData.VehicleCost,
    formData.DownPayment,
    formData.EMITenure,
    formData.ProcessingFees,
    formData.InterestRate
  ])

  const calculateLoanSummary = () => {
    validateFinanceValues(parsedFinanceValues)
    return calculateReducingRateSummary(parsedFinanceValues)
  }

  const handleLoanSummaryClick = () => {
    try {
      const summary = calculateLoanSummary()
      setLoanSummaryError('')
      if (typeof onLoanSummary === 'function') {
        onLoanSummary(summary)
      }
    } catch (err) {
      setLoanSummaryError(err.message || 'Unable to calculate loan summary.')
    }
  }

  const handlePreviewDocument = useCallback((file) => {
    if (!file || !file.FileLibraryId) return
    const url = buildFileDownloadUrl(file.FileLibraryId)
    window.open(url, '_blank')
  }, [])

  useEffect(() => {
    if (FinanceRegisterData) {
      const hasSalesCustomer = Boolean(FinanceRegisterData.SalesId && FinanceRegisterData.CustomerId)
      const customerName = hasSalesCustomer
        ? (FinanceRegisterData.CustomerMasterName || FinanceRegisterData.CustomerName || '')
        : (FinanceRegisterData.CustomerName || '')
      const mobileNo = hasSalesCustomer
        ? (FinanceRegisterData.CustomerMasterMobile || FinanceRegisterData.MobileNo || '')
        : (FinanceRegisterData.MobileNo || '')
      const emailId = hasSalesCustomer
        ? (FinanceRegisterData.CustomerMasterEmail || FinanceRegisterData.EmailId || '')
        : (FinanceRegisterData.EmailId || '')
      const customerAddress = hasSalesCustomer
        ? (FinanceRegisterData.CustomerAddress || '')
        : (FinanceRegisterData.CustomerAddress || '')

      const next = {
        ...INITIAL_FORM_STATE,
        ...FinanceRegisterData,
        CustomerName: customerName,
        MobileNo: mobileNo,
        EmailId: emailId,
        CustomerAddress: customerAddress,
        VehicleLabel: FinanceRegisterData.VehicleLabel || '',
        VehicleCost: FinanceRegisterData.VehicleCost ?? FinanceRegisterData.SalesVehicleCost ?? '',
        ChassisNo: FinanceRegisterData.ChassisNo || '',
        EngineNo: FinanceRegisterData.EngineNo || '',
        DownPayment: FinanceRegisterData.DownPayment ?? FinanceRegisterData.SalesDownPayment ?? '',
        EMITenure: FinanceRegisterData.EMITenure ?? FinanceRegisterData.SalesEMITenure ?? '',
        ProcessingFees: FinanceRegisterData.ProcessingFees ?? FinanceRegisterData.SalesProcessingFees ?? '',
        InterestRate: FinanceRegisterData.InterestRate ?? FinanceRegisterData.SalesInterestRate ?? '',
        EMIAmount: FinanceRegisterData.EMIAmount ?? FinanceRegisterData.SalesEMIAmount ?? ''
      }
      setFormData(next)
      setSelectedFinancePartnerId(FinanceRegisterData.FinancePartnerId ?? null)
    }
  }, [FinanceRegisterData])

  useEffect(() => {
    fetchFinancePartners()
  }, [fetchFinancePartners])

  useEffect(() => {
    if (typeof onLoanSummaryApply === 'function') {
      onLoanSummaryApply((summary) => {
        updateFieldValue('EMIAmount', summary?.monthlyEmi ? String(summary.monthlyEmi) : '')
      })
    }
  }, [onLoanSummaryApply, updateFieldValue])

  useEffect(() => {
    fetchCustomerDocuments(formData.CustomerId || FinanceRegisterData?.CustomerId)
  }, [fetchCustomerDocuments, formData.CustomerId, FinanceRegisterData?.CustomerId])

  const selectedSale = useMemo(() => {
    if (!formData.SalesId) return null
    const label = `${formData.SalesNumber || ''}${formData.CustomerName ? ` (${formData.CustomerName})` : ''}`
    return { value: formData.SalesId, label }
  }, [formData.SalesId, formData.SalesNumber, formData.CustomerName])

  const isSalesEntry = Boolean(formData.SalesId)

  const vehicleOption = useMemo(() => {
    if (!formData.VehicleId) return null
    return { value: formData.VehicleId, label: formData.VehicleLabel || 'Vehicle' }
  }, [formData.VehicleId, formData.VehicleLabel])

  return (
    <>
      <div className="row">
        <div className="col-lg-12 col-sm-12 col-12 mb-3">
          <label className="form-label">Customer Sales</label>
          <Select value={selectedSale} options={selectedSale ? [selectedSale] : []} isDisabled />
        </div>

        <div className="col-12">
          <div className="border rounded p-3 mb-3">
            <h6 className="mb-3">Customer Details</h6>
            <div className="row">
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Customer Name</label>
                <input type="text" className="form-control" value={formData.CustomerName} disabled />
              </div>
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Email Address</label>
                <input type="text" className="form-control" value={formData.EmailId} disabled />
              </div>
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Phone Number</label>
                <input type="text" className="form-control" value={formData.MobileNo} disabled />
              </div>
              <div className="col-lg-12 col-sm-12 col-12 mb-3">
                <label className="form-label">Address</label>
                <input type="text" className="form-control" value={formData.CustomerAddress} disabled />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="border rounded p-3 mb-3">
            <h6 className="mb-3">Documents</h6>
            <FileList
              files={customerFiles}
              onDownload={handlePreviewDocument}
              showRemove={false}
              showDownload={true}
              downloadLabel="Preview"
            />
          </div>
        </div>

        <div className="col-12">
          <div className="border rounded p-3 mb-3">
            <h6 className="mb-3">Vehicle Details</h6>
            <div className="row">
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Vehicle</label>
                <Select value={vehicleOption} options={vehicleOption ? [vehicleOption] : []} isDisabled />
              </div>
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Vehicle Cost</label>
                <input type="text" className="form-control" value={formData.VehicleCost} disabled />
              </div>
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Chassis No</label>
                <input type="text" className="form-control" value={formData.ChassisNo} disabled />
              </div>
              <div className="col-lg-4 col-sm-6 col-12 mb-3">
                <label className="form-label">Engine No</label>
                <input type="text" className="form-control" value={formData.EngineNo} disabled />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <FinanceDetails
            financePartners={financePartners}
            financePartnersError={financePartnersError}
            selectedFinancePartnerId={selectedFinancePartnerId}
            onFinancePartnerClick={handleFinancePartnerClick}
            downPaymentValue={formData.DownPayment}
            emiTenureValue={formData.EMITenure}
            processingFeesValue={formData.ProcessingFees}
            interestRateValue={formData.InterestRate}
            emiAmountValue={formData.EMIAmount}
            onDownPaymentChange={(e) => updateFieldValue('DownPayment', e.currentTarget.value)}
            onEmiTenureChange={(e) => updateFieldValue('EMITenure', e.currentTarget.value)}
            onProcessingFeesChange={(e) => updateFieldValue('ProcessingFees', e.currentTarget.value)}
            onInterestRateChange={(option) => updateFieldValue('InterestRate', option ? option.value : '')}
            onEmiAmountChange={(e) => updateFieldValue('EMIAmount', e.currentTarget.value)}
            interestRateOptions={interestRateOptions}
            interestRateLoading={interestRateLoading}
            selectedInterestRate={selectedInterestRate}
            onCalculateEmi={handleLoanSummaryClick}
            loanSummaryError={loanSummaryError}
            disabled={isSalesEntry}
          />
        </div>

        <div className="col-lg-4 col-sm-6 col-12 mb-3">
          <label className="form-label">Loan Application No</label>
          <input
            type="text"
            className="form-control"
            name="LoanApplicationNo"
            value={formData.LoanApplicationNo}
            onChange={(e) => updateFieldValue('LoanApplicationNo', e.currentTarget.value)}
          />
        </div>
      </div>
    </>
  )
}

export default EditFinanceRegister